/**
 * The submit path of a second-factor code field — shared by `<MfaCodeInput>` and `<MfaBackupEntry>`.
 *
 * ## One path, decided by where the field is rendered
 *
 *   - INSIDE a `<form>`, the form is the path, and `onSubmit` is not passed. Enter is the browser's
 *     own implicit submission; auto-submit calls `form.requestSubmit()`. Either way the host's
 *     `<form onSubmit={(event) => …}>` runs once, with a real, cancelable submit event, exactly as it
 *     does for a click on its submit button. The host keeps `event.preventDefault()`.
 *   - OUTSIDE a form, `onSubmit()` is the path. It is called with NO argument, on Enter and on
 *     auto-submit: a host with no form has no submit event to be handed.
 *
 * Measured over the eleven hand-built second-factor inputs this field replaces (D12 census): seven
 * sit in a form whose handler takes the submit event, four have no form and a handler that takes
 * none. Each keeps its handler as written.
 *
 * A wrong pairing is refused at mount, because either one fails quietly otherwise: `onSubmit` inside
 * a form gives two paths to one handler, and a field with neither can never be submitted from the
 * keyboard.
 *
 * ## The keyboard never submits an incomplete code
 *
 * Enter submits a complete code only, and never while the field is busy. Outside a form the field
 * does that itself. Inside one it stops the keydown of an incomplete code, so the browser's implicit
 * submission cannot send a code that is bound to be refused (and to spend an attempt). A click on
 * the host's submit button is the host's own gate.
 *
 * ## One submit per code
 *
 * `onSubmitted` is told of every submit the field can see: any submit of its form (Enter, the host's
 * button, auto-submit) and its own Enter outside a form. `<MfaCodeInput>` passes its auto-submit's
 * `disarm`, so a code submitted by hand is never submitted again by the timer.
 *
 * @module @puffin-core/mfa-ui/hooks/useCodeEntrySubmit
 */

import { useCallback, useEffect } from 'react';

/**
 * Why this pairing of form and `onSubmit` cannot work, or null when it can.
 * @param {string} component - the field's name, for the message
 * @param {HTMLFormElement|null} form - the form the input belongs to
 * @param {boolean} hasOnSubmit - whether the host passed `onSubmit`
 * @returns {string|null}
 */
function submitPathRefusal(component, form, hasOnSubmit) {
  if (form && hasOnSubmit) {
    return `${component}: inside a <form> the form is the one submit path. Remove the \`onSubmit\` prop; the form's own onSubmit receives the submit event.`;
  }
  if (!form && !hasOnSubmit) {
    return `${component}: outside a <form> there is nothing to submit. Pass \`onSubmit\`, or render the input inside the form that takes the code.`;
  }
  return null;
}

/**
 * Raise the host's submit for a field: its form's when it has one, else the `onSubmit` it was given.
 * @param {HTMLInputElement} input - the field's input
 * @param {Function} [onSubmit] - the host's handler, outside a form
 * @returns {void}
 */
export function requestHostSubmit(input, onSubmit) {
  if (input.form) {
    input.form.requestSubmit();
    return;
  }
  onSubmit();
}

/**
 * @param {Object} options
 * @param {{ current: HTMLInputElement|null }} options.inputRef - the field's input
 * @param {string} options.component - the field's name, for the refusal
 * @param {boolean} options.complete - the code is complete and the field is not busy
 * @param {Function} [options.onSubmit] - the host's handler, outside a form
 * @param {Function} [options.onSubmitted] - told of every submit the field can see
 * @returns {(event: React.KeyboardEvent<HTMLInputElement>) => void} the input's keydown handler
 */
export function useCodeEntrySubmit({ inputRef, component, complete, onSubmit, onSubmitted }) {
  const hasOnSubmit = Boolean(onSubmit);

  useEffect(() => {
    const refusal = submitPathRefusal(component, inputRef.current.form, hasOnSubmit);
    if (refusal) throw new Error(refusal);
  }, [component, hasOnSubmit, inputRef]);

  useEffect(() => {
    const form = inputRef.current.form;
    if (!form || !onSubmitted) return undefined;
    form.addEventListener('submit', onSubmitted);
    return () => form.removeEventListener('submit', onSubmitted);
  }, [inputRef, onSubmitted]);

  return useCallback(
    (event) => {
      if (event.key !== 'Enter') return;
      if (event.currentTarget.form) {
        if (!complete) event.preventDefault();
        return;
      }
      if (!complete) return;
      event.preventDefault();
      onSubmitted?.();
      onSubmit();
    },
    [complete, onSubmit, onSubmitted],
  );
}
